/**
 * Client-side text model for the RENDERED docx-preview DOM. Kept in PARITY with the
 * browser tokenizer's model (src/lib/docx/tokenize.ts) so that mark-time ambiguity
 * detection and highlight placement match what the tokenizer will do at save time.
 *
 * The matching itself is NOT duplicated here — it lives in src/lib/docx/text-locate.ts and
 * is shared with the tokenizer. This module only supplies the DOM-derived haystack and
 * chooses the policy to apply to an ambiguous result.
 */

import { ERR_AMBIGUOUS, locateText, normalize, occurrenceAt } from '@/lib/docx/text-locate';
import {
  COMPANY_SUB_KINDS,
  SUB_KINDS,
  type CompanySubKind,
  type MarkedVariable,
  type SubKind,
} from './types';

export const NAME_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

/**
 * The marked-occurrence tiebreak, in the shape `locateText` consumes. Pass a variable's own
 * pair through wherever it is located, or repeated text resolves to whichever copy scored
 * best rather than the one the admin actually picked.
 */
export interface Occurrence {
  occurrenceIndex?: number | null;
  occurrenceCount?: number | null;
}

/** A marked variable's occurrence pair, ready to hand to `locateInBody`. */
export function occurrenceOf(v: {
  occurrenceIndex?: number | null;
  occurrenceCount?: number | null;
}): Occurrence {
  return { occurrenceIndex: v.occurrenceIndex, occurrenceCount: v.occurrenceCount };
}

export function toIdentifier(s: string): string {
  let id = s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!id) id = 'field';
  if (/^[0-9]/.test(id)) id = `_${id}`;
  return id;
}

/**
 * Tags whose subtree the tokenizer (w:body only) never walks: headers, footers,
 * foot/endnote bodies (docx-preview renders notes as <li>), and footnote reference
 * marks (<sup>). Selecting/anchoring inside these can't be tokenized.
 */
const EXCLUDED_TAGS = new Set(['HEADER', 'FOOTER', 'SUP', 'LI']);

/** Block/line tags that imply a paragraph boundary — the tokenizer injects "\n" at each. */
const BLOCK_TAGS = new Set([
  'P', 'DIV', 'SECTION', 'ARTICLE', 'TABLE', 'TR', 'TD', 'TH', 'UL', 'OL',
  'BLOCKQUOTE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'HR',
]);

function isExcludedElement(el: Element): boolean {
  if (EXCLUDED_TAGS.has(el.tagName)) return true;
  const cls = typeof el.className === 'string' ? el.className : '';
  return /footnote|endnote/i.test(cls);
}

/** True if the node sits inside a header, footer, or footnote — unreachable by the tokenizer. */
export function isExcludedFromBody(node: Node, root: HTMLElement): boolean {
  let el: HTMLElement | null =
    node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as HTMLElement);
  while (el && el !== root) {
    if (isExcludedElement(el)) return true;
    el = el.parentElement;
  }
  return false;
}

export interface BodyModel {
  nodes: Array<{ node: Text; start: number }>;
  text: string;
}

/**
 * Flatten the rendered body into one string + a text-node offset map, mirroring the
 * tokenizer: skip headers/footers/notes, inject a phantom "\n" at block/line
 * boundaries and " " for tab-like classes. Phantoms live only in the string — never in
 * `nodes` — so a trimmed selection's start/end always resolve to a real text node.
 */
export function buildBodyModel(container: HTMLElement): BodyModel {
  const nodes: Array<{ node: Text; start: number }> = [];
  let text = '';

  const pushGap = (ch: string) => {
    if (text.length > 0 && !/\s$/.test(text)) text += ch;
  };

  const walk = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      if (isExcludedElement(el)) return;
      const tag = el.tagName;
      if (tag === 'BR') {
        pushGap('\n');
        return;
      }
      const cls = typeof el.className === 'string' ? el.className : '';
      if (/(^|[-\s])tab($|[-\s])/i.test(cls)) {
        pushGap(' ');
        return;
      }
      const block = BLOCK_TAGS.has(tag);
      if (block) pushGap('\n');
      let child = el.firstChild;
      while (child) {
        walk(child);
        child = child.nextSibling;
      }
      if (block) pushGap('\n');
      return;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      const content = node.textContent ?? '';
      if (!content) return;
      nodes.push({ node: node as Text, start: text.length });
      text += content;
    }
  };

  walk(container);
  return { nodes, text };
}

/** Re-exported so DOM-side callers get the tokenizer's exact whitespace handling. */
export { normalize } from '@/lib/docx/text-locate';

/**
 * The [start, end) span a live Range covers within the body model.
 *
 * Derived from the range's INTERSECTION with the model's text nodes, never from the length
 * of `Selection.toString()`. Those two disagree: docx-preview renders a tab as a real
 * `&emsp;` text node that `buildBodyModel` skips in favour of a single phantom space, and
 * the browser inserts its own newlines at block boundaries that the model deduplicates. Add
 * `toString().length` to a start offset and every selection containing a tab or a line
 * break lands the end offset somewhere else entirely — which is how `contextAfter` used to
 * be captured from the wrong part of the document.
 *
 * Also handles element-node range boundaries (triple-click, dragging past a paragraph end),
 * which a text-node-only lookup cannot resolve at all.
 */
export function bodySpanOfRange(model: BodyModel, range: Range): { start: number; end: number } | null {
  let start = -1;
  let end = -1;
  for (const seg of model.nodes) {
    const len = seg.node.textContent?.length ?? 0;
    if (len === 0) continue;
    try {
      if (range.comparePoint(seg.node, 0) > 0) continue; // node begins after the range ends
      if (range.comparePoint(seg.node, len) < 0) continue; // node ends before the range begins
    } catch {
      continue; // detached or cross-document node — not part of this selection
    }
    const from = range.startContainer === seg.node ? range.startOffset : 0;
    const to = range.endContainer === seg.node ? range.endOffset : len;
    if (start < 0) start = seg.start + from;
    end = seg.start + to;
  }
  return start >= 0 && end > start ? { start, end } : null;
}

/**
 * Locate a stored selection in the rendered body — the BEST-EFFORT policy over
 * {@link locateText}: ambiguity is ignored and the best-scoring (on a full tie, earliest)
 * match is returned, so a highlight still appears and a signature box still gets placed.
 * `createAnchorResolver` layers paragraph tiers on top precisely because of this.
 */
export function locateInBody(
  model: BodyModel,
  selectedText: string,
  contextBefore: string,
  contextAfter: string,
  occurrence?: Occurrence
): { rawStart: number; rawEnd: number } | null {
  return locateText(model.text, selectedText, {
    contextBefore,
    contextAfter,
    ...occurrence,
  }).span;
}

/** Build a DOM Range spanning [rawStart, rawEnd) within the body model. */
export function rangeFromBodyOffsets(
  model: BodyModel,
  rawStart: number,
  rawEnd: number
): Range | null {
  let startNode: Text | undefined;
  let startOff = 0;
  let endNode: Text | undefined;
  let endOff = 0;
  for (const seg of model.nodes) {
    const len = seg.node.textContent?.length ?? 0;
    const segEnd = seg.start + len;
    if (!startNode && rawStart >= seg.start && rawStart < segEnd) {
      startNode = seg.node;
      startOff = rawStart - seg.start;
    }
    if (rawEnd > seg.start && rawEnd <= segEnd) {
      endNode = seg.node;
      endOff = rawEnd - seg.start;
    }
    if (startNode && endNode) break;
  }
  if (!startNode || !endNode) return null;
  try {
    const range = document.createRange();
    range.setStart(startNode, startOff);
    range.setEnd(endNode, endOff);
    return range;
  } catch {
    return null;
  }
}

/**
 * Mark-time validation: catch, in context, the ambiguity/overlap problems that would
 * otherwise surface only as a deferred save-time tokenizer failure. Returns a
 * human-readable reason to BLOCK the mark, or null to allow it. Only blocks on
 * problems detectable reliably here (ambiguity mirrors the tokenizer's locate(); and
 * overlap with another marked span). A span we can't find is NOT blocked — the
 * tokenizer is the source of truth at save time.
 */
export function checkPlaceable(
  root: HTMLElement | null,
  variables: MarkedVariable[],
  selectedText: string,
  contextBefore: string,
  contextAfter: string,
  ignoreIndex: number | null,
  occurrence?: Occurrence
): string | null {
  if (!root || !root.firstElementChild) return null; // not rendered — tokenizer validates on save
  const model = buildBodyModel(root);

  const outcome = locateText(model.text, selectedText, {
    contextBefore,
    contextAfter,
    ...occurrence,
  });
  if (outcome.empty) return null;
  if (outcome.ambiguous) return ERR_AMBIGUOUS;

  const loc = outcome.span;
  if (loc) {
    const needle = normalize(selectedText.trim()).norm;
    for (let i = 0; i < variables.length; i++) {
      if (i === ignoreIndex) continue;
      const other = variables[i];
      if (normalize(other.selectedText.trim()).norm === needle) continue;
      const otherLoc = locateInBody(
        model,
        other.selectedText,
        other.contextBefore,
        other.contextAfter,
        occurrenceOf(other)
      );
      if (!otherLoc) continue;
      if (loc.rawStart < otherLoc.rawEnd && otherLoc.rawStart < loc.rawEnd) {
        return `This overlaps the field “${other.label}” — pick text that doesn't include another field.`;
      }
    }
  }
  return null;
}

/**
 * Give fields marked before occurrence tracking existed an ordinal, read off the rendered
 * document — but ONLY where the rendered document resolves them unambiguously.
 *
 * The gate is the point. An ordinal is treated as authoritative downstream, so recording a
 * guessed one would convert a loud save-time failure into a silent mis-placement. Where the
 * render is unsure, the field is left exactly as it was and keeps resolving by context.
 *
 * Returns the same array when nothing changed, so callers can skip a needless state write.
 */
export function backfillOccurrences(
  root: HTMLElement | null,
  variables: MarkedVariable[]
): MarkedVariable[] {
  if (!root || !root.firstElementChild) return variables;
  if (!variables.some((v) => v.occurrenceIndex == null)) return variables;

  const model = buildBodyModel(root);
  let changed = false;
  const next = variables.map((v) => {
    if (v.occurrenceIndex != null) return v;
    const outcome = locateText(model.text, v.selectedText, {
      contextBefore: v.contextBefore,
      contextAfter: v.contextAfter,
    });
    if (outcome.ambiguous || !outcome.span) return v;
    const occurrence = occurrenceAt(model.text, v.selectedText, outcome.span.rawStart);
    if (!occurrence) return v;
    changed = true;
    return { ...v, occurrenceIndex: occurrence.index, occurrenceCount: occurrence.count };
  });
  return changed ? next : variables;
}

// ---------------------------------------------------------------------------
// Identifier helpers (namespace shared by field names, person keys, child names)
// ---------------------------------------------------------------------------

export function uniqueName(
  variables: MarkedVariable[],
  base: string,
  ignoreName?: string,
  reserved?: Iterable<string>
): string {
  // A plain field must not collide with an existing variable name OR a reserved name — the
  // latter carries person KEYS, since a person key (e.g. "acme_corp") shares the same
  // namespace as field names even though its own token is "acme_corp__company". Without this,
  // marking a field over text that matches a person (e.g. to mirror that person's value)
  // auto-generates a clashing name and the commit rejects it.
  const taken = new Set(variables.filter((v) => v.name !== ignoreName).map((v) => v.name));
  if (reserved) for (const r of reserved) taken.add(r);
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}_${i}`)) i++;
  return `${base}_${i}`;
}

/** Every child-name suffix an entity key could claim, across both entity kinds. */
const ALL_SUB_SUFFIXES: string[] = [...SUB_KINDS, ...COMPANY_SUB_KINDS];

/**
 * Allocate an entity key (person OR company) that collides with nothing: no existing
 * variable name, no other entity key, and no child name either kind could generate.
 *
 * Person and company keys share ONE namespace, which is what makes `${key}__${sub}`
 * unambiguous even though both kinds have a `name` sub-field — the key identifies the
 * entity, and the parent row's Type says which kind it is.
 *
 * `reserved` must therefore carry BOTH persons' and companies' keys.
 */
export function uniqueEntityKey(
  variables: MarkedVariable[],
  reserved: Iterable<string>,
  base: string
): string {
  const taken = new Set<string>([...variables.map((v) => v.name), ...reserved]);
  const free = (k: string) =>
    !taken.has(k) && ALL_SUB_SUFFIXES.every((s) => !taken.has(`${k}__${s}`));
  if (free(base)) return base;
  let i = 2;
  while (!free(`${base}_${i}`)) i++;
  return `${base}_${i}`;
}

export function childName(key: string, sub: SubKind): string {
  return `${key}__${sub}`;
}

export function companyChildName(key: string, sub: CompanySubKind): string {
  return `${key}__${sub}`;
}

export function subKindsTaken(
  variables: MarkedVariable[],
  key: string,
  ignoreName?: string
): Set<SubKind> {
  return new Set(
    variables
      .filter((v) => v.personKey === key && v.subKind && v.name !== ignoreName)
      .map((v) => v.subKind as SubKind)
  );
}

export function companySubKindsTaken(
  variables: MarkedVariable[],
  key: string,
  ignoreName?: string
): Set<CompanySubKind> {
  return new Set(
    variables
      .filter((v) => v.companyKey === key && v.companySubKind && v.name !== ignoreName)
      .map((v) => v.companySubKind as CompanySubKind)
  );
}
